'use strict';

/**
 * /ts/theme.ts - light/dark/system theme toggle. html/layout.html's inline
 * head script already resolves the mode before first paint (avoiding a
 * flash); the applyTheme() call at the bottom of this file covers a script
 * that ran before that inline one did, or a cleared storage.
 */

/** What the operator picked: light, dark, or system (follow the OS). */
type ThemeChoice = 'light' | 'dark' | 'system';

const THEME_CHOICE_KEY = 'theme';
const darkQuery = window.matchMedia('(prefers-color-scheme: dark)');

/** Cycle order for the single-button control: the default first. */
const THEME_ORDER: ThemeChoice[] = ['system', 'light', 'dark'];

/** Symbol per choice; the button shows exactly the active mode. */
const THEME_ICONS: Record<ThemeChoice, string> = {
	system: '#circle-half-stroke',
	light: '#sun',
	dark: '#moon'
};

const THEME_LABELS: Record<ThemeChoice, string> = {
	system: 'System',
	light: 'Light',
	dark: 'Dark'
};

function currentThemeChoice(): ThemeChoice {
	const choice = document.documentElement.dataset.themeChoice;
	return choice === 'light' || choice === 'dark' || choice === 'system' ? choice : 'system';
}

/** data-theme is always the resolved mode; CSS never queries the OS. */
function applyTheme(choice: ThemeChoice, persist: boolean): void {
	const root = document.documentElement;
	root.dataset.themeChoice = choice;
	root.dataset.theme = choice === 'system' ? (darkQuery.matches ? 'dark' : 'light') : choice;
	if (persist) setStoredValue(THEME_CHOICE_KEY, choice);
	syncThemeButton();
}

/** Point every toggle button (the header's, always hidden below 400px wide,
 * and Settings' own copy, always shown) at the active mode's icon and name
 * it for assistive tech (the icon itself is aria-hidden, so the label is the
 * only accessible name). Settings' copy also carries a visible text label
 * (.theme-toggle-label) beside the icon -- the header's icon-only button has
 * no such element, so this is a no-op there. */
function syncThemeButton(): void {
	const choice = currentThemeChoice();
	const label = `Color theme: ${THEME_LABELS[choice]}`;
	for (const button of document.querySelectorAll<HTMLButtonElement>('.theme-toggle')) {
		button.querySelector('use')?.setAttribute('href', THEME_ICONS[choice]);
		button.setAttribute('aria-label', label);
		button.title = label;
		const text = button.querySelector('.theme-toggle-label');
		if (text) text.textContent = THEME_LABELS[choice];
	}
}

for (const button of document.querySelectorAll<HTMLButtonElement>('.theme-toggle')) {
	button.addEventListener('click', () => {
		const next = THEME_ORDER[(THEME_ORDER.indexOf(currentThemeChoice()) + 1) % THEME_ORDER.length];
		applyTheme(next, true);
	});
}

// Follow the OS while the operator is on System, without a reload.
darkQuery.addEventListener('change', () => {
	if (currentThemeChoice() === 'system') applyTheme('system', false);
});

/** Resolve the mode before the first render so a dark-mode reload does not
 * flash the light palette. The head script already did this for the inline
 * case; this covers a script that ran before it (or a cleared storage). */
applyTheme(currentThemeChoice(), false);
